import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CDN_URL, PLACEHOLDER_IMG } from '../ConfigPublic';
import { Button, Header, Input, Message } from 'react-frontier';
import { TransitionGroup, Dropdown, Icon } from 'semantic-ui-react';
import { useCart, useWebdata, useUser } from '../HooksPublic';
import { addCommas } from '../UtilPublic';
import { CartItem, SimpleProgress } from '@cocoan/components';
import Product from './Product';
import { WebCategory, ShopPayload } from '../ClassesPublic';
import classNames from 'classnames';
import API from '../APIPublic';
import slugify from 'slugify';
import Quantity from './Quantity';

interface NavbarItem {
	text: string,
	href?: string,
	items?: {
		text: string,
		href: string,
	}[]
}

const NAVBAR_ITEMS_BEFORE: NavbarItem[] = [
	{ text: 'Rewards', href: 'rewards' },
	{ text: 'Nosotros', href: 'nosotros' },
	{ text: 'Visítanos', href: 'visitanos' },
];



var Navbar = () => {
	var { pathname } = useLocation();
	var { user, login, logout } = useUser();
	const IS_CHECKOUT = pathname.startsWith('/checkout');
	var { cart, items, updateCart, hideCart, showCart, showing, total: cartTotal, addToCart } = useCart();
	var [sidemenu, setSidemenu] = useState<boolean>(false);
	var [searchBar, setSearchBar] = useState<boolean>(false);
	var [searchValue, setSearchValue] = useState<string>('');
	var [megaOpen, setMegaOpen] = useState<string | null>(null);
	var [megaLeft, setMegaLeft] = useState<number>(0);
	var [categories, setCategories] = useState<WebCategory[]>(null);
	var [activeCategory, setActiveCategory] = useState<number>(null);
	var [categoryProducts, setCategoryProducts] = useState<Record<number, ShopPayload['products']>>({});
	var [loadingProducts, setLoadingProducts] = useState<boolean>(false);
	var [popularProducts, setPopularProducts] = useState<ShopPayload['products'] | null>(null);
	var [recommended, setRecommended] = useState<ShopPayload['products'] | null>(null);
	var [mobilePersonalizadosOpen, setMobilePersonalizadosOpen] = useState<boolean>(false);
	var megaTimeout = useRef<ReturnType<typeof setTimeout>>(null);
	var searchRef = useRef<HTMLInputElement>(null);
	var navigate = useNavigate();
	var authUrl = API.getAuthUrl() || '/auth';

	useEffect(() => {
		if (showing && recommended === null) {
			API.getShopData([], '', 0, 10).then(res => {
				if (!res.error && res.data) {
					setRecommended(res.data.products);
				}
			});
		}
	}, [showing, recommended]);
	useEffect(() => {
		API.getAllCategories().then(res => {
			if (!res.error && res.data) setCategories(res.data);
		});
	}, []);

	useEffect(() => {
		if (searchBar && popularProducts === null) {
			API.getShopData([], '', 0, 4).then(res => {
				if (!res.error && res.data) {
					setPopularProducts(res.data.products);
				}
			});
		}
	}, [searchBar, popularProducts]);

	var loadCategoryProducts = useCallback((category_id: number) => {
		if (categoryProducts[category_id]) return;
		setLoadingProducts(true);
		API.getShopData([category_id], null, 0).then(res => {
			if (!res.error && res.data) {
				setCategoryProducts(prev => ({ ...prev, [category_id]: res.data.products }));
			}
		}).finally(() => setLoadingProducts(false));
	}, [categoryProducts]);

	var handleCategoryHover = (category_id: number) => {
		setActiveCategory(category_id);
		loadCategoryProducts(category_id);
	};

	var openMega = (type: string) => (e: React.MouseEvent<HTMLElement>) => {
		if (megaTimeout.current) clearTimeout(megaTimeout.current);
		var rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
		setMegaLeft(rect.left);
		setMegaOpen(type);
	};

	var closeMega = () => {
		megaTimeout.current = setTimeout(() => {
			setMegaOpen(null);
		}, 180);
	};

	var keepMegaOpen = () => {
		if (megaTimeout.current) clearTimeout(megaTimeout.current);
	};

	var toggleCart = () => {
		if (showing) hideCart();
		else showCart();
		setSidemenu(false);
		setSearchBar(false);
		setMegaOpen(null);
	};

	var toggleSidemenu = () => {
		setSidemenu(!sidemenu);
		setSearchBar(false);
		hideCart();
		setMegaOpen(null);
	};

	var toggleSearchBar = () => {
		setSearchBar(!searchBar);
		hideCart();
		setSidemenu(false);
		setMegaOpen(null);
		setTimeout(() => {
			searchRef.current?.focus();
		}, 320);
	};

	var hideAll = () => {
		hideCart();
		setSidemenu(false);
		setSearchBar(false);
		setMegaOpen(null);
	};

	var searchProducts = () => {
		navigate(`/search?q=${encodeURIComponent(searchValue)}`);
		hideAll();
	};

	var updateCartQuantity = (product_id: number) => {
		return (v: { target: { value: any } }) => {
			updateCart(product_id, parseInt(v.target.value));
		};
	};

	var displayedRecommended = useMemo(() => {
		if (!recommended) return [];
		var inCartIds = cart.map(a => a.product_id);
		return recommended.filter(a => !inCartIds.includes(a.product_id)).slice(0, 2);
	}, [recommended, cart]);

	var activeCategoryProducts = activeCategory ? (categoryProducts[activeCategory] || []) : [];

	if (IS_CHECKOUT) {
		return <div className="co navbar">
			<div className="navbar-content" style={{ height: 25 }}>
				<div className="menu">
					<Link className="item" style={{ color: 'white' }} to="/shop">
						<i className="arrow left icon" style={{ color: 'white' }}></i>
					</Link>
				</div>
				<div className="left">
					<Link className="item" style={{ color: 'white' }} to="/shop">
						<i className="arrow left icon" style={{ color: 'white' }}></i> Regresar
					</Link>
				</div>
				<Link className="logo" to="/">
					<img src={`${CDN_URL}/static/logo/Logo_White.png`} alt="COCOAN" />
				</Link>
				<div className="right"></div>
			</div>
		</div>;
	}

	return <>
		<div className="co navbar">
			<div className="navbar-content">
				<div className="menu">
					<div className="item" onClick={toggleSidemenu}>
						<i style={{ color: 'white' }} className="hamburger icon"></i>
					</div>
				</div>
				<div className="left">

					<Dropdown
						item text='Chocolate'
						style={{ color: 'white' }}
						className='desktop'
						onMouseEnter={openMega('chocolate')}
						onMouseLeave={closeMega}
					/>
					<Dropdown
						item text='Personalizados'
						style={{ color: 'white' }}
						className='desktop'
						onMouseEnter={openMega('personalizados')}
						onMouseLeave={closeMega}
					/>

					{NAVBAR_ITEMS_BEFORE.map((a, i) => (
						a.items ? (
							<Dropdown item text={a.text} style={{ color: 'white' }} className='desktop' key={`navbar-before-${i}`} simple>
								<Dropdown.Menu>
									{a.items.map((b, j) => (
										<Dropdown.Item key={`dropdown-itm-${j}`} as={Link} to={`/${b.href}`}>
											{b.text}
										</Dropdown.Item>
									))}
								</Dropdown.Menu>
							</Dropdown>
						) : (
							<Link className="item desktop" style={{ color: 'white' }} to={`/${a.href}`} key={`navbar-before-${i}`}>{a.text}</Link>
						)
					))}
				</div>
				<Link className="logo" to="/">
					<img src={`${CDN_URL}/static/logo/Logo_White.png`} alt="COCOAN" />
				</Link>
				<div className="right">
					<div className="item" onClick={toggleSearchBar}>
						<i style={{ color: 'white' }} className="search icon"></i>
					</div>
					<div className="item" onClick={toggleCart}>
						<i style={{ color: 'white' }} className="bag shopping icon"></i>
						{items > 0 && (
							<div className="tag">{items}</div>
						)}
					</div>
						{(login && user) ? (
							<Dropdown
								item
								className='desktop'
								key='navbar-account'
								style={{ color: 'white' }}
								simple
								trigger={<span style={{ color: 'white' }}><Icon name='user' /> {user.first_name || 'Mi cuenta'}</span>}
							>
								<Dropdown.Menu>
									<Dropdown.Item as={Link} to='/account'>Mi cuenta</Dropdown.Item>
									<Dropdown.Item onClick={() => { logout(); hideAll(); window.location.href = authUrl; }}>Cerrar sesión</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						) : (
							<Link style={{ gap: 5, color: 'white' }} className="item desktop" to={authUrl} key='navbar-login'>
								<Icon name='sign in' style={{ color: 'white' }} />Iniciar sesión
							</Link>
						)}
				</div>
			</div>
		</div>

		{megaOpen === 'chocolate' && (
			<div className="co mega-menu">
				<div className="mega-backdrop" />
				<div
					className="mega-categories"
					style={{ paddingLeft: megaLeft }}
					onMouseEnter={keepMegaOpen}
					onMouseLeave={closeMega}
				>
					{categories === null && (
						<div className="mega-loading">Cargando…</div>
					)}
					{categories !== null && (
						<div className="mega-grid">
							{categories.slice(0, 7).map(cat => (
								<Link
									key={`mega-cat-${cat.category_id}`}
									className="mega-cat-item"
									to={`/shop/${cat.category_id}-${slugify(cat.category_name, { lower: true })}`}
									onClick={() => setMegaOpen(null)}
								>
									<span>{cat.category_name}</span>
								</Link>
							))}
							<div className="mega-actions">
								<Link
									to="/shop"
									className="mega-see-all"
									onClick={() => setMegaOpen(null)}
								>
									Ver más <i className="arrow right icon" />
								</Link>
							</div>
						</div>
					)}
				</div>
			</div>
		)}

		{megaOpen === 'personalizados' && (
			<div className="co mega-menu">
				<div className="mega-backdrop" />
				<div
					className="mega-categories"
					style={{ paddingLeft: megaLeft }}
					onMouseEnter={keepMegaOpen}
					onMouseLeave={closeMega}
				>
					<Link
						className="mega-cat-item"
						to="/personalizados-sociales"
						onClick={() => setMegaOpen(null)}
					>
						<span>Social</span>
					</Link>
					<Link
						className="mega-cat-item"
						to="/personalizados-empresariales"
						onClick={() => setMegaOpen(null)}
					>
						<span>Empresarial</span>
					</Link>
				</div>
			</div>
		)}

		<TransitionGroup animation={'fade'} duration={200}>
			{(showing || sidemenu || searchBar) && (
				<div className={classNames('co dimmer', {
					nav: sidemenu
				})} onClick={hideAll}></div>
			)}
		</TransitionGroup>

		<TransitionGroup animation={'fade down'} duration={300}>
			{searchBar && (
				<div className={'co searchbar'}>
					<div className="search-header">
						<div className="search-input-wrapper">
							<i className="search icon"></i>
							<input
								ref={searchRef}
								value={searchValue}
								onChange={(e) => setSearchValue(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') searchProducts();
								}}
								placeholder='Busca chocolates, tabletas, fresas, tus favoritos...'
							/>
						</div>
						<div className="close-btn" onClick={hideAll}>
							<i className="remove icon"></i>
						</div>
					</div>

					<div className="search-suggestions">
						<div className="products-column">
							<h4>Productos populares</h4>
							<div className="popular-products">
								{!popularProducts ? (
									(new Array(4).fill(0)).map((a, i) => (
										<Product placeholder product={null} key={`sugg-prodplh-${i}`} />
									))
								) : (
									popularProducts.map(a => (
										<div key={`sugg-prod-${a.product_id}`} onClick={hideAll}>
											<Product product={a} />
										</div>
									))
								)}
							</div>
						</div>
					</div>
				</div>
			)}
		</TransitionGroup>

		<TransitionGroup animation={'fade right'} duration={300}>
			{sidemenu && (
				<div className={'co sidemenu'}>
					{/* Personalizados Dropdown */}
					<div className={classNames('item dropdown', { open: mobilePersonalizadosOpen })}>
						<div className="content" onClick={() => setMobilePersonalizadosOpen(!mobilePersonalizadosOpen)}>
							<span className="text">Personalizados</span>
							<i className={classNames('chevron down icon', { 'up': mobilePersonalizadosOpen })}></i>
						</div>
						{mobilePersonalizadosOpen && (
							<div className="menu">
								<Link className="item" to="/personalizados-sociales" onClick={toggleSidemenu}>Sociales</Link>
								<Link className="item" to="/personalizados-empresariales" onClick={toggleSidemenu}>Empresariales</Link>
							</div>
						)}
					</div>

					{NAVBAR_ITEMS_BEFORE.map((a, i) => (
						<Link className='item' key={`sidebar-before-${i}`} to={a.href} onClick={toggleSidemenu}>{a.text}</Link>
					))}
					<Link className='item' to='/shop' onClick={toggleSidemenu}>Comprar</Link>
					{(login && user) ? (
						<>
							<Link className='item' to='/account' onClick={toggleSidemenu}>
								<Icon name='user' /> {user.first_name || 'Mi cuenta'}
							</Link>
							<div className='item' onClick={() => { logout(); hideAll(); window.location.href = authUrl; }}>
								<Icon name='sign out' /> Cerrar sesión
							</div>
						</>
					) : (
						<Link className='item' to={authUrl} onClick={toggleSidemenu}>
							<Icon name='sign in' /> Iniciar sesión
						</Link>
					)}
				</div>
			)}
		</TransitionGroup>

		<TransitionGroup animation={'fade left'} duration={300}>
			{showing && (
				<div className={classNames('co cart', {
					empty: !cart || cart.length == 0
				})}>
					<div className="head">
						<div className="text">Carrito</div>
						<div className="close">
							<i className="remove icon" onClick={toggleCart}></i>
						</div>
					</div>
					{!cart || cart.length == 0 ? (
						<Header text='Tu carrito está vacío' size='small' iconName='frown' style={{ marginTop: 20 }} actions={(
							<Button text="Cerrar" color='black' onClick={toggleCart} style={{ fontSize: 16, marginTop: 10 }} />
						)} />
					) : <>
						<div className="items">
							{cart.map(a => {
								return <CartItem
									product={a}
									amount={a.quantity}
									image={`${CDN_URL}/products/${a.product_id}/0.webp`}
									placeholderImage={PLACEHOLDER_IMG}
									actions={<>
										{a.redeem_points ? <><div style={{fontSize: 13}}>¡Producto a canjear!</div></> : <Quantity quantity={a.quantity} onChange={(q)=>updateCartQuantity(a.product_id)({target:{value:q}})} />}
										<Button iconName='trash' basic icon onClick={() => updateCartQuantity(a.product_id)({ target: { value: 0 } })} />
									</>}
								/>
							})}
						</div>
						{displayedRecommended.length > 0 && (
							<div className="cart-recommended" style={{ backgroundColor: '#F2F2F2', padding: '12px 10px', marginTop: 15, borderRadius: 8 }}>
									<div className="font-graphik" style={{ fontSize: 18, fontWeight: 'bold', color: '#111', marginBottom: 12 }}>Un deleite más...</div>
									<div className="recommended-list">
										{displayedRecommended.map((a, i) => (
											<CartItem
												key={`recom-${a.product_id}-${i}`}
												product={a as any}
												amount={1}
												image={`${CDN_URL}/products/${a.product_id}/0.webp`}
												placeholderImage={PLACEHOLDER_IMG}
												actions={
													<Button
														size="tiny"
														color="black"
														text="Agregar"
														onClick={() => addToCart(a.product_id, 1, false)}
													/>
												}
											/>
										))}
									</div>
								</div>
							)}
						<div className="totals">
							<div className="divider" style={{ marginBottom: 10 }}></div>
							<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
								<div>Subtotal</div>
								<div>${addCommas(cartTotal, true, true)}</div>
							</div>
							<div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginBottom: 20 }}>
								<div>Total</div>
								<div>${addCommas(cartTotal, true, true)}</div>
							</div>
							{cartTotal <= 0 && cart.length > 0 ? (
								<Message type="error" text="Necesitas un producto con costo para poder canjearlo" style={{ marginBottom: 15 }} />
							) : (
								<Button size="big" fluid color="black" text="Completar compra" as={Link} to="/checkout" onClick={toggleCart} />
							)}
						</div>
					</>}
				</div>
			)}
		</TransitionGroup>
	</>;
}

export default Navbar;